import { type FC } from "react";
import { useFormikContext } from "formik";

import { useRolesForContext } from "@vega-api/extended/auth/roles/useRolesForContext";
import { type RoleContext } from "@vega-api/generated/types/roleContext";
import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { EmptyFundsList } from "../../../../components/EmptyFundsList";
import { FundCell } from "../../../../components/FundCell";
import { RoleSelector } from "../../../../components/RoleSelector";
import { type groupPermissionsBySubject } from "../../../../helpers/groupPermissions";

type Props = {
  roleContext: RoleContext;
  permissions: ReturnType<typeof groupPermissionsBySubject>;
  onRoleChange?: (role: RoleNew, subjectId: string) => void;
  onRemoveAccess?: (subjectId: string) => void;
};

export const RolesTab: FC<Props> = ({
  permissions,
  onRoleChange,
  onRemoveAccess,
  roleContext,
}) => {
  const { values } = useFormikContext<{
    roles: Partial<Record<string, { roleId: string; subjectId: string }>>;
  }>();

  const { getRoleOptionsAsRoles } = useRolesForContext(roleContext);
  const roleOptions = getRoleOptionsAsRoles(true);

  if (permissions.length === 0) {
    return (
      <EmptyFundsList
        description={m.userManagement_permissions_noOfferings()}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto max-h-[400px]">
        <div className="flex flex-col gap-4">
          {permissions.map((permission) => {
            const selectedRoleId = isTruthy(permission.subjectId)
              ? values.roles[permission.subjectId]?.roleId
              : undefined;

            return (
              <div
                className="flex items-center gap-3 justify-between"
                key={permission.subjectId}
              >
                <FundCell
                  assetClass={permission.assetClass}
                  subjectName={permission.subjectName}
                />
                <RoleSelector
                  onChange={(option) => {
                    if (onRoleChange && permission.subjectId) {
                      onRoleChange(option, permission.subjectId);
                    }
                  }}
                  onRemoveAccess={() => {
                    if (onRemoveAccess && permission.subjectId) {
                      onRemoveAccess(permission.subjectId);
                    }
                  }}
                  options={roleOptions}
                  showRemoveButton={false}
                  value={selectedRoleId}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
